import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Notifications, Orders } from '../../store/slices';
import { setUpdateOrder } from '../../api';
import { AppDialog } from '../ui';
import { useStyles } from './styles';
import { ORDER_STATUSES } from '../../lib/orderStatus';
import { errorMessage } from '../../lib/errorMessage';

/** Statuses an operator may set by hand. */
const EDITABLE_STATUS_IDS = [1, 2, 3, 5, 12];

const ShippingMethodCode = [
    { title: 'USPS Ground Advantage', value: 'USPS Ground Advantage' },
    { title: 'USPS Priority Mail', value: 'USPS Priority Mail' },
    { title: 'UPSMI Parcel Select', value: 'UPSMI Parcel Select' },
];

/** Edit status, shipping method and the recipient address; only changed fields are sent. */
interface OrderEditFormProps {
    data: any;
    isPopUp: boolean;
    popUpHandler: (closed?: boolean) => void;
}

export default function OrderEditForm({ data, isPopUp, popUpHandler }: OrderEditFormProps) {
    const classes = useStyles();
    const [formData, setFormData] = useState<any>({});
    const [recipient, setRecipientFormData] = useState<any>({});
    const { id, companyId } = useParams();
    const dispatch = useDispatch();
    const [pending, setPending] = useState(false);

    const getOrder = () => {
        dispatch<any>(Orders.getOrder({ id: id || '', companyId: companyId || '' }));
    };

    const reset = () => {
        setFormData({});
        setRecipientFormData({});
    };

    const close = () => {
        reset();
        popUpHandler(true);
    };

    const updateOrder = async () => {
        if (pending) return;
        setPending(true);
        let payload = { ...formData };
        if (Object.keys(recipient).length !== 0) payload = { ...payload, recipient };
        try {
            await setUpdateOrder({
                id,
                companyId,
                data: payload,
                onSuccess: () => {
                    dispatch(Notifications.setNotifications({ text: 'Order updated', type: 'success' }));
                    reset();
                    popUpHandler(true);
                    getOrder();
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: errorMessage(error), type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setRecipientFormData({ ...recipient, [name]: value });
    };
    const handleSelectChange = (event: { target: { name: string; value: unknown } }) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const address = data?.recipient ?? {};
    const dirty = Object.keys(formData).length > 0 || Object.keys(recipient).length > 0;

    return (
        <AppDialog
            open={Boolean(isPopUp)}
            onClose={close}
            title={`Edit order ${data?.orderNumber ?? id}`}
            description="Only the fields you change are sent to the client integration."
            pending={pending}
            maxWidth="sm"
            actions={(
                <>
                    <Button variant="outlined" onClick={close}>Cancel</Button>
                    <Button variant="contained" disabled={!dirty || pending} onClick={updateOrder}>Save changes</Button>
                </>
            )}
        >
            <div className={classes.form}>
                <div className={classes.sectionTitle}>Order</div>
                <div className={classes.grid}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="edit-status-label">Order status</InputLabel>
                        <Select
                            labelId="edit-status-label"
                            label="Order status"
                            name="orderStatusId"
                            defaultValue={data?.orderStatusId ?? ''}
                            disabled={Boolean(data?.onHold)}
                            onChange={handleSelectChange}
                        >
                            {ORDER_STATUSES.filter((status) => EDITABLE_STATUS_IDS.includes(status.id)).map((status) => (
                                <MenuItem key={status.id} value={status.id}>{status.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel id="edit-shipping-label">Shipping method</InputLabel>
                        <Select
                            labelId="edit-shipping-label"
                            label="Shipping method"
                            name="shippingMethodCode"
                            defaultValue={data?.shippingMethodCode || ''}
                            onChange={handleSelectChange}
                            disabled={companyId === '27768'}
                        >
                            {ShippingMethodCode.map((method) => (
                                <MenuItem key={method.value} value={method.value}>{method.title}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.sectionTitle}>Recipient</div>
                <div className={classes.grid}>
                    <TextField size="small" label="Name" name="name" defaultValue={address.name || ''} onChange={handleInputChange} fullWidth className={classes.wide} />
                    <TextField size="small" label="Street 1" name="street1" defaultValue={address.street1 || ''} onChange={handleInputChange} fullWidth className={classes.wide} />
                    <TextField size="small" label="Street 2" name="street2" defaultValue={address.street2 || ''} onChange={handleInputChange} fullWidth className={classes.wide} />
                    <TextField size="small" label="City" name="city" defaultValue={address.city || ''} onChange={handleInputChange} fullWidth />
                    <TextField size="small" label="State" name="state" defaultValue={address.state || ''} onChange={handleInputChange} fullWidth />
                    <TextField size="small" label="Postal code" name="postalCode" defaultValue={address.postalCode || ''} onChange={handleInputChange} fullWidth />
                    <TextField size="small" label="Country" name="country" defaultValue={address.country || ''} onChange={handleInputChange} fullWidth />
                </div>
            </div>
        </AppDialog>
    );
}
